"use client";

import { useEffect, useMemo, useState, useCallback } from "react";
import { useRouter } from "next/navigation";
import { useDispatch, useSelector } from "react-redux";
import {
  ArrowLeft,
  User,
  Pencil,
  Headset,
  MapPin,
  Info,
  Shield,
  LogOut,
  ChevronRight,
  Loader2,
} from "lucide-react";
import dynamic from "next/dynamic";
import { useAuth } from "../context/authContext";
import { clearAuth, fetchCustomerProfile, updateCustomerProfile } from "../config/redux/slices/authAndAddressSlice";
import { toast } from "react-toastify";

const LoginModal = dynamic(() => import("../Components/LoginModal"), {
  ssr: false,
});

const menuItems = [
  {
    key: "help",
    title: "Help & Support",
    subtitle: "Get help with your queries",
    icon: Headset,
    href: "/contact-sortkaro",
  },
  {
    key: "addresses",
    title: "Manage Addresses",
    icon: MapPin,
    href: "/my-addresses",
  },
  {
    key: "about",
    title: "About Sortkaro",
    icon: Info,
    href: "/about-sortkaro",
  },
  {
    key: "privacy",
    title: "Privacy Policy",
    icon: Shield,
    href: "/privacy-policy",
  },
];

export default function ProfilePage() {
  const router = useRouter();
  const dispatch = useDispatch();
  const { isAuthenticated, logout } = useAuth();

  const profile = useSelector((state) => state.auth?.Userprofile || null);
  const authLoading = useSelector((state) => state.auth?.loading || false);

  const [showLogin, setShowLogin] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [form, setForm] = useState({ name: "", email: "" });

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchCustomerProfile());
    }
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    setForm({
      name: profile?.name || "",
      email: profile?.email || "",
    });
  }, [profile?.name, profile?.email]);

  const mobileNumber = useMemo(() => {
    return profile?.mobile || localStorage.getItem("mobile") || "";
  }, [profile?.mobile]);

  const handleLogout = useCallback(() => {
    logout();
    dispatch(clearAuth());
    localStorage.removeItem("profile");
    localStorage.removeItem("mobile");
    toast.success("Logged out successfully");
    router.push("/");
  }, [dispatch, logout, router]);

  const handleSaveProfile = useCallback(async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }

    try {
      setSavingProfile(true);
      await dispatch(
        updateCustomerProfile({
          name: form.name.trim(),
          email: form.email.trim(),
        })
      ).unwrap();
      toast.success("Profile updated");
      setIsEditOpen(false);
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setSavingProfile(false);
    }
  }, [dispatch, form]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen mt-40 px-4 py-10">
        <div className="max-w-md mx-auto bg-white rounded-3xl border border-gray-200 p-6 text-center">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-2">Profile</h1>
          <p className="text-gray-500 mb-6">Please login to open your profile page.</p>
          <button
            onClick={() => setShowLogin(true)}
            className="w-full h-12 rounded-2xl bg-[#0b5fb7] text-white font-semibold hover:bg-[#0a4f99] transition-colors"
          >
            Login
          </button>
        </div>
        <LoginModal show={showLogin} onClose={() => setShowLogin(false)} />
      </div>
    );
  }

  return (
    <div className="min-h-screen mt-14 px-4 py-10">
      <main className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => router.back()}
            className="w-12 h-12 rounded-2xl bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-50"
          >
            <ArrowLeft className="h-6 w-6 text-gray-700" />
          </button>
          <h1 className="text-[20px] md:text-2xl font-semibold text-gray-900">Profile</h1>
          <div className="w-12" />
        </div>

        {authLoading && !profile ? (
          <div className="bg-white rounded-3xl border border-gray-200 p-6 animate-pulse">
            <div className="h-5 w-40 bg-gray-200 rounded mb-3" />
            <div className="h-4 w-56 bg-gray-100 rounded" />
          </div>
        ) : (
          <>
            <section className="bg-white rounded-3xl border border-gray-200 p-4 md:p-6 mb-5">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 md:w-20 md:h-20 rounded-2xl md:rounded-3xl bg-[#edf2f9] flex items-center justify-center">
                  <User className="h-7 w-7 md:h-10 md:w-10 text-[#0b5fb7]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[20px] md:text-xl font-semibold text-gray-900 truncate">
                    {profile?.name || "Guest User"}
                  </p>
                  <p className="text-[16px] md:text-lg text-gray-500 mt-1">{mobileNumber || "No mobile number"}</p>
                </div>
                <button
                  onClick={() => setIsEditOpen(true)}
                  className="w-11 h-11 md:w-14 md:h-14 rounded-xl md:rounded-2xl bg-[#f1f5fb] flex items-center justify-center hover:bg-[#e8eef8]"
                  aria-label="Edit profile"
                >
                  <Pencil className="h-5 w-5 md:h-6 md:w-6 text-[#0b5fb7]" />
                </button>
              </div>
            </section>

            <section className="bg-white rounded-3xl border border-gray-200 p-4 md:p-6 mb-5">
              <h2 className="text-[20px] md:text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
              <button
                onClick={() => router.push(menuItems[0].href)}
                className="w-full flex items-center gap-4 py-2 text-left"
              >
                <div className="w-11 h-11 md:w-14 md:h-14 rounded-xl md:rounded-2xl bg-[#edf2f9] flex items-center justify-center">
                  <Headset className="h-5 w-5 md:h-7 md:w-7 text-[#0b5fb7]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[16px] md:text-lg text-gray-900 font-medium">{menuItems[0].title}</p>
                  <p className="text-[14px] md:text-sm text-gray-500">{menuItems[0].subtitle}</p>
                </div>
                <ChevronRight className="h-5 w-5 md:h-7 md:w-7 text-gray-300" />
              </button>
            </section>

            <section className="bg-white rounded-3xl border border-gray-200 p-4 md:p-6 mb-5">
              <h2 className="text-[20px] md:text-xl font-semibold text-gray-900 mb-2">Account Settings</h2>
              <div className="divide-y divide-gray-100">
                {menuItems.slice(1).map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.key}
                      onClick={() => router.push(item.href)}
                      className="w-full flex items-center gap-4 py-4 md:py-5 text-left"
                    >
                      <div className="w-11 h-11 md:w-14 md:h-14 rounded-xl md:rounded-2xl bg-[#edf2f9] flex items-center justify-center">
                        <Icon className="h-5 w-5 md:h-7 md:w-7 text-[#0b5fb7]" />
                      </div>
                      <p className="flex-1 text-[16px] md:text-lg text-gray-900 font-medium">{item.title}</p>
                      <ChevronRight className="h-5 w-5 md:h-7 md:w-7 text-gray-300" />
                    </button>
                  );
                })}
              </div>
            </section>

            <button
              onClick={handleLogout}
              className="w-full h-14 md:h-16 rounded-3xl border-2 border-[#0b5fb7] text-[#0b5fb7] text-[16px] md:text-xl font-semibold bg-white hover:bg-[#f0f7ff] transition-colors flex items-center justify-center gap-3"
            >
              <LogOut className="h-5 w-5 md:h-7 md:w-7" />
              Logout
            </button>
          </>
        )}
      </main>

      {isEditOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm p-4 flex items-center justify-center">
          <div className="w-full max-w-md bg-white rounded-3xl p-5 border border-gray-200">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Edit Profile</h3>
            <div className="space-y-3">
              <input
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Full name"
                className="w-full h-12 px-4 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
              <input
                value={form.email}
                onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                placeholder="Email"
                type="email"
                className="w-full h-12 px-4 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
            <div className="flex gap-3 mt-5">
              <button
                onClick={() => setIsEditOpen(false)}
                className="flex-1 h-11 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveProfile}
                disabled={savingProfile}
                className="flex-1 h-11 rounded-xl bg-[#0b5fb7] text-white hover:bg-[#0a4f99] disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {savingProfile ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      <LoginModal show={showLogin} onClose={() => setShowLogin(false)} />
    </div>
  );
}
